import styled from 'styled-components';

import {
    DeliveryState,
    GetDeliveryQuery,
    useMarkDeliveriesAsAbortedMutation,
    useMarkDeliveriesAsArchivedMutation,
    useMarkDeliveriesAsDeliveredMutation,
    useMarkDeliveriesAsInProgressMutation,
    useMarkDeliveriesAsPackagingMutation,
    useMarkDeliveriesAsReadyMutation,
} from 'data/__generated__';

import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { StatusTag } from '../components/StatusTag';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Loader, LoaderModeType } from 'components/Loader';
import { confirmPopup } from 'components/Popups/Confirm';

export const StatesSection = ({ delivery }: { delivery: NonNullable<GetDeliveryQuery['delivery']> }) => {
    const { _id: deliveryId, state, stateHistory } = delivery;

    // Ces mutations ne renvoient qu'un `DeliveryFragment`, plus maigre que la sélection de
    // GET_DELIVERY (le rangement créé au passage en livrée n'y arrive par exemple qu'avec son `_id`).
    // Sans re-fetch, la query de la page reste incomplète : Apollo continue de servir le dernier
    // résultat complet et les boutons de transition suivants restent désactivés.
    const stateMutationOptions = {
        variables: { deliveryIds: [deliveryId] },
        refetchQueries: ['GET_DELIVERY'],
        awaitRefetchQueries: true,
    };

    const [markDeliveriesAsAborted, { loading: markAsAbortedLoading }] =
        useMarkDeliveriesAsAbortedMutation(stateMutationOptions);
    const [markDeliveriesAsArchived, { loading: markAsArchivedLoading }] =
        useMarkDeliveriesAsArchivedMutation(stateMutationOptions);
    const [markDeliveriesAsDelivered, { loading: markAsDeliveredLoading }] =
        useMarkDeliveriesAsDeliveredMutation(stateMutationOptions);
    const [markDeliveriesAsInProgress, { loading: markAsInProgressLoading }] =
        useMarkDeliveriesAsInProgressMutation(stateMutationOptions);
    const [markDeliveriesAsPackaging, { loading: markAsPackagingLoading }] =
        useMarkDeliveriesAsPackagingMutation(stateMutationOptions);
    const [markDeliveriesAsReady, { loading: markAsReadyLoading }] =
        useMarkDeliveriesAsReadyMutation(stateMutationOptions);

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Passer en préparation',
            action: markDeliveriesAsPackaging,
            loading: markAsPackagingLoading,
            isDisabled: state !== DeliveryState.Created,
        },
        {
            label: 'Marquer comme prête',
            action: markDeliveriesAsReady,
            loading: markAsReadyLoading,
            isDisabled: state !== DeliveryState.Packaging,
        },
        {
            label: 'Passer en livraison',
            action: markDeliveriesAsInProgress,
            loading: markAsInProgressLoading,
            isDisabled: state !== DeliveryState.Ready,
        },
        {
            label: 'Marquer comme livrée',
            action: markDeliveriesAsDelivered,
            loading: markAsDeliveredLoading,
            isDisabled: ![
                DeliveryState.Packaging,
                DeliveryState.Ready,
                DeliveryState.InProgress,
                DeliveryState.NotDelivered,
            ].includes(state),
        },
        {
            label: 'Annuler la livraison',
            action: markDeliveriesAsAborted,
            loading: markAsAbortedLoading,
            isDisabled: ![
                DeliveryState.Created,
                DeliveryState.InProgress,
                DeliveryState.Packaging,
                DeliveryState.Ready,
                DeliveryState.NotDelivered,
            ].includes(state),
        },
        {
            label: 'Archiver la livraison',
            action: markDeliveriesAsArchived,
            loading: markAsArchivedLoading,
            isDisabled: ![DeliveryState.Delivered, DeliveryState.NotDelivered, DeliveryState.Aborted].includes(state),
        },
    ];

    return (
        <SectionContainer title="Historique de statuts" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <StateHistory records={formattedStateHistory} />
                </SectionColumn>
                <SectionColumn>
                    <StatusTag state={state} showHeading />
                </SectionColumn>
                <SectionColumn>
                    {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                        const { label, action, isDisabled, loading } = stateChange;

                        async function updateState() {
                            const hasConfirmed = await confirmPopup({
                                title: 'Confirmer',
                                message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
                            });
                            if (hasConfirmed) {
                                action();
                            }
                        }

                        return (
                            <TotemPrimaryButton key={index} type="button" onClick={updateState} disabled={isDisabled}>
                                {label}
                                {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : null}
                            </TotemPrimaryButton>
                        );
                    })}
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const Link = styled.a`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;
